import { clearSession, getSessionToken, refreshSession } from './session';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

const baseUrl = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';

type ApiOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  auth?: boolean;
  signal?: AbortSignal;
};

async function request<T>(path: string, opts: ApiOptions, attempt = 0): Promise<T> {
  const headers = new Headers({ Accept: 'application/json' });
  if (opts.body !== undefined) headers.set('Content-Type', 'application/json');
  if (opts.auth !== false) {
    const token = getSessionToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }

  const res = await fetch(`${baseUrl}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  if (res.status === 401 && opts.auth !== false && attempt === 0) {
    try {
      await refreshSession();
    } catch (err) {
      clearSession();
      throw new ApiError(401, err instanceof Error ? err.message : 'session refresh failed');
    }
    return request<T>(path, opts, attempt + 1);
  }

  if (!res.ok) {
    const payload = await res.json().catch(() => null);
    const message: string =
      (payload as { error?: { message?: string } } | null)?.error?.message ??
      `request failed with ${res.status}`;
    const code = (payload as { error?: { code?: string } } | null)?.error?.code;
    throw new ApiError(res.status, message, code);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, opts: Omit<ApiOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts: Omit<ApiOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...opts, method: 'POST', body }),
};

export function getApiBaseUrl(): string {
  return baseUrl;
}

export function getWsBaseUrl(): string {
  const origin = baseUrl || window.location.origin;
  return origin.replace(/^http/, 'ws');
}
